import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface EnhancedFinancialRecord {
  id: string;
  user_id: string;
  amount: number;
  type: 'tuition_debt' | 'tuition_payment' | 'salary_earned' | 'salary_payment';
  description?: string;
  lesson_id?: string;
  phase_id?: string;
  group_id?: string;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'approved' | 'rejected';
  // Related data
  profiles?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  phases?: {
    name: string;
    phase_number: number;
  };
  groups?: {
    name: string;
  };
  lessons?: {
    lesson_number: number;
    lesson_date: string;
    topic?: string;
  };
}

interface FinancialStatistics {
  total_pending_salaries: number;
  total_approved_salaries: number;
  total_pending_tuition: number;
  total_approved_tuition: number;
  lecturers_awaiting_payment: number;
  students_with_debt: number;
}

export const useEnhancedFinancialData = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [records, setRecords] = useState<EnhancedFinancialRecord[]>([]);
  const [statistics, setStatistics] = useState<FinancialStatistics | null>(null);
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    dateRange: 'all',
    search: ''
  });
  const channelRef = useRef<any>(null);

  const fetchFinancialStatistics = async () => {
    try {
      console.log('Fetching financial statistics...');
      const { data, error } = await supabase.rpc('get_financial_statistics');
      if (error) {
        console.error('RPC error:', error);
        throw error;
      }
      
      console.log('Raw statistics data:', data);
      
      if (data && data.length > 0) {
        setStatistics(data[0]);
        console.log('Statistics set successfully:', data[0]);
      } else {
        console.log('No statistics data received');
        setStatistics({
          total_pending_salaries: 0,
          total_approved_salaries: 0,
          total_pending_tuition: 0,
          total_approved_tuition: 0,
          lecturers_awaiting_payment: 0,
          students_with_debt: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching financial statistics:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել ֆինանսական վիճակագրությունը",
        variant: "destructive"
      });
    }
  };

  const fetchUserBalance = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('get_user_balance', {
        user_id: user.id
      });

      if (error) throw error;
      setBalance(data || 0);
    } catch (error) {
      console.error('Error fetching user balance:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել բալանսը",
        variant: "destructive"
      });
    }
  };

  const fetchEnhancedFinancialRecords = async () => {
    try {
      console.log('Fetching enhanced financial records...');
      setLoading(true);

      // Fetch financial records first
      const { data: recordsData, error: recordsError } = await supabase
        .from('financial_records')
        .select('*')
        .order('created_at', { ascending: false });

      if (recordsError) {
        console.error('Query error:', recordsError);
        throw recordsError;
      }

      console.log('Basic financial records fetched:', recordsData?.length || 0, 'records');

      // If we have records, fetch related data separately
      if (recordsData && recordsData.length > 0) {
        const userIds = [...new Set(recordsData.map(r => r.user_id).filter(Boolean))];
        const phaseIds = [...new Set(recordsData.map(r => r.phase_id).filter(Boolean))];
        const groupIds = [...new Set(recordsData.map(r => r.group_id).filter(Boolean))];
        const lessonIds = [...new Set(recordsData.map(r => r.lesson_id).filter(Boolean))];

        // Fetch related data in parallel
        const [profilesData, phasesData, groupsData, lessonsData] = await Promise.all([
          userIds.length > 0 ? supabase.from('profiles').select('id, first_name, last_name, email').in('id', userIds) : Promise.resolve({ data: [] }),
          phaseIds.length > 0 ? supabase.from('phases').select('id, name, phase_number').in('id', phaseIds) : Promise.resolve({ data: [] }),
          groupIds.length > 0 ? supabase.from('groups').select('id, name').in('id', groupIds) : Promise.resolve({ data: [] }),
          lessonIds.length > 0 ? supabase.from('lessons').select('id, lesson_number, lesson_date, topic').in('id', lessonIds) : Promise.resolve({ data: [] })
        ]);

        // Create lookup maps
        const profilesMap = new Map((profilesData.data || []).map(p => [p.id, p]));
        const phasesMap = new Map((phasesData.data || []).map(p => [p.id, p]));
        const groupsMap = new Map((groupsData.data || []).map(g => [g.id, g]));
        const lessonsMap = new Map((lessonsData.data || []).map(l => [l.id, l]));

        // Enhance records with related data
        const enhancedRecords = recordsData.map(record => ({
          ...record,
          profiles: record.user_id ? profilesMap.get(record.user_id) : null,
          phases: record.phase_id ? phasesMap.get(record.phase_id) : null,
          groups: record.group_id ? groupsMap.get(record.group_id) : null,
          lessons: record.lesson_id ? lessonsMap.get(record.lesson_id) : null,
        }));

        console.log('Enhanced financial records created successfully:', enhancedRecords.length, 'records');
        setRecords(enhancedRecords as any);
      } else {
        console.log('No financial records found');
        setRecords([]);
      }
    } catch (error) {
      console.error('Error fetching enhanced financial records:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել ֆինանսական տվյալները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRecordStatus = async (recordId: string, status: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('financial_records')
        .update({ 
          status,
          approved_by: user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', recordId);

      if (error) throw error;

      await fetchEnhancedFinancialRecords();
      await fetchUserBalance();
      await fetchFinancialStatistics();

      toast({
        title: "Հաջողություն",
        description: `Գրանցումը ${status === 'approved' ? 'հաստատված' : 'մերժված'} է`,
      });
    } catch (error) {
      console.error('Error updating record status:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց թարմացնել կարգավիճակը",
        variant: "destructive"
      });
    }
  };

  const bulkUpdateStatus = async (recordIds: string[], status: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('financial_records')
        .update({ 
          status,
          approved_by: user?.id,
          approved_at: new Date().toISOString()
        })
        .in('id', recordIds);

      if (error) throw error;

      await fetchEnhancedFinancialRecords();
      await fetchUserBalance();
      await fetchFinancialStatistics();

      toast({
        title: "Հաջողություն",
        description: `${recordIds.length} գրանցում ${status === 'approved' ? 'հաստատված' : 'մերժված'} են`,
      });
    } catch (error) {
      console.error('Error bulk updating records:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց թարմացնել գրանցումները",
        variant: "destructive"
      });
    }
  };

  const deleteRecord = async (recordId: string) => {
    try {
      const { error } = await supabase
        .from('financial_records')
        .delete()
        .eq('id', recordId);

      if (error) throw error;

      await fetchEnhancedFinancialRecords();
      await fetchUserBalance();
      await fetchFinancialStatistics();

      toast({
        title: "Հաջողություն",
        description: "Գրանցումը ջնջված է",
      });
    } catch (error) {
      console.error('Error deleting record:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ջնջել գրանցումը",
        variant: "destructive"
      });
    }
  };

  const addManualRecord = async (recordData: {
    user_id: string;
    amount: number;
    type: string;
    description: string;
    phase_id?: string;
    group_id?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('financial_records')
        .insert({
          ...recordData,
          status: 'pending'
        });

      if (error) throw error;

      await fetchEnhancedFinancialRecords();
      await fetchFinancialStatistics();

      toast({
        title: "Հաջողություն",
        description: "Ֆինանսական գրանցում ավելացված է",
      });

      return true;
    } catch (error) {
      console.error('Error adding manual record:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ավելացնել գրանցումը",
        variant: "destructive"
      });
      return false;
    }
  };

  // Filter records based on current filters
  const filteredRecords = records.filter(record => {
    // Type filter
    if (filters.type !== 'all' && record.type !== filters.type) return false;
    
    // Status filter
    if (filters.status !== 'all' && record.status !== filters.status) return false;
    
    // Search filter
    if (filters.search && !record.description?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !record.profiles?.first_name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !record.profiles?.last_name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !record.groups?.name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !record.phases?.name?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('hy-AM', {
      style: 'currency',
      currency: 'AMD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount));
  };

  const getBalanceColor = (amount: number): string => {
    if (amount > 0) return 'text-green-400';
    if (amount < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'tuition_debt': return 'Վարձավճարի պարտք';
      case 'tuition_payment': return 'Վարձավճարի վճարում';
      case 'salary_earned': return 'Աշխատավարձ';
      case 'salary_payment': return 'Աշխատավարձի վճարում';
      default: return type;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending': return 'Սպասման մեջ';
      case 'approved': return 'Հաստատված';
      case 'rejected': return 'Մերժված';
      default: return status;
    }
  };

  useEffect(() => {
    console.log('useEnhancedFinancialData effect triggered, user:', user?.id);
    
    const initializeData = async () => {
      try {
        await fetchEnhancedFinancialRecords();
        await fetchFinancialStatistics();
        if (user) {
          await fetchUserBalance();
        }
        console.log('All financial data initialized successfully');
      } catch (error) {
        console.error('Error initializing financial data:', error);
      }
    };

    // Initialize real-time subscription
    const setupRealtimeSubscription = () => {
      console.log('Setting up realtime subscription for financial_records');
      
      channelRef.current = supabase
        .channel('financial-records-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'financial_records'
          },
          (payload) => {
            console.log('Realtime change detected:', payload);
            
            // Refresh data when changes occur
            fetchEnhancedFinancialRecords();
            fetchFinancialStatistics();
            if (user) {
              fetchUserBalance();
            }
          }
        )
        .subscribe((status) => {
          console.log('Realtime subscription status:', status);
        });
    };

    initializeData();
    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (channelRef.current) {
        console.log('Cleaning up realtime subscription');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user]);

  return {
    records: filteredRecords,
    allRecords: records,
    statistics,
    balance,
    loading,
    filters,
    setFilters,
    fetchEnhancedFinancialRecords,
    fetchUserBalance,
    fetchFinancialStatistics,
    updateRecordStatus,
    bulkUpdateStatus,
    deleteRecord,
    addManualRecord,
    formatAmount,
    getBalanceColor,
    getTypeLabel,
    getStatusLabel
  };
};